/**
 * InstallAppButton — PWA Install Prompt CTA (persistent landing button)
 *
 * SPEC-111 (P-173 — QA SITE, severidad ALTA — UX PWA):
 *   El Founder desinstaló la PWA de su celular. Cerró el InstallBanner con
 *   "Ahora no" (X) hace días → el banner tiene cooldown de 7 días
 *   (`pwa-install-dismissed` localStorage) → no vuelve a aparecer. No hay
 *   ningún otro punto en la UI pública para disparar la instalación. El
 *   Founder está bloqueado: no puede reinstalar la app sin esperar 7 días o
 *   sin limpiar localStorage manualmente.
 *
 *   Fix: botón "Instalar App" persistente en la landing principal que
 *   reusa el hook `useInstallPrompt` existente. Se renderiza SIEMPRE que
 *   sea elegible (isInstallable OR chromeReportsInstalled) y NO esté ya
 *   instalada.
 *
 * Diferencia con InstallBanner (componente hermano en `pwa/`):
 *   - SIN cooldown 7 días (siempre visible si es elegible — no lee
 *     `pwa-install-dismissed`).
 *   - SIN gate de visitas (no requiere 2+ visitas como el banner).
 *   - SIN timer 30s (visible inmediatamente al montar, no espera).
 *   - NO se suprime en /login, /register, /forgot-password, /admin/login
 *     (es un CTA en la LANDING pública `/` — no en auth pages; el banner
 *     sí se suprime porque es `position: fixed bottom-4 z-50` y tapa el
 *     botón submit — AUDIT-070-009; este botón NO es fixed, está inline
 *     en el flujo del hero y el CTA final, no tapa nada).
 *
 * Reusa el hook `useInstallPrompt` existente — cero lógica nueva (§6
 * Anti-Invención). El hook expone:
 *   - `isInstallable`: true si deferredPrompt disponible OR
 *     chromeReportsInstalled (WebAPK stale — camino de recuperación P-159).
 *   - `isInstalled`: true solo con señales REALES (display-mode standalone
 *     o evento appinstalled — P-164).
 *   - `install()`: dispara `deferredPrompt.prompt()` o, sin deferredPrompt
 *     (WebAPK stale), muestra la guía accionable P-159 ("ábrela desde tu
 *     lista; si no la encuentras, desinstálala desde Ajustes e instala de
 *     nuevo").
 *
 * Graceful degradation (Firefox/Safari/desktop):
 *   Si el navegador no soporta PWA install (ningún beforeinstallprompt
 *   dispara Y Chrome no reporta WebAPK) → `isInstallable=false` → el
 *   botón NO se renderiza (null). No hay false positive.
 *
 * Mount gate (P-147 anti-hydration):
 *   `useInstallPrompt` lee `window` / `localStorage` / `navigator` a
 *   través de su useEffect (con guards de SSR) — pero el initializer del
 *   useState puede invocar `consumeCapturedPrompt()` que lee el stash del
 *   módulo (que se setea solo si `typeof window !== 'undefined'`). Para
 *   evitar cualquier divergencia SSR/client, este botón NO renderiza nada
 *   hasta que `useEffect` confirme el mount (patrón P-147, same que
 *   `InstallBanner`, `OfflineIndicator`, `theme-toggle.tsx` — 4E-MED-002).
 *
 * Trazabilidad §6: SPEC-111 §3 Task A, SPEC-025 §2.2/§3.3 (hook reused),
 *                   P-159 (guidance reusada), P-164 (isInstalled real),
 *                   P-147 (mount gate), AUDIT-070-009 (banner suppress
 *                   intacto — este botón NO es fixed, no tapa nada).
 *
 * OFF-LIMITS: no modifica InstallBanner.tsx ni useInstallPrompt.ts — los
 * reusa as-is.
 *
 * P-196 (SPEC-130 — QA SITE Sprint 58, Founder Anderson 2026-09-06):
 *   Tras desinstalar la PWA, Chrome Android puede reportar el WebAPK como
 *   instalado (vía `getInstalledRelatedApps()`) aunque el usuario ya lo
 *   haya desinstalado. En ese estado `beforeinstallprompt` NO dispara → el
 *   hook marca `webapkStale=true` (tras 2s de gracia). El click en este
 *   botón, cuando `webapkStale=true`, abre el modal `StaleWebApkDialog` en
 *   lugar de llamar `install()` (que mostraría solo un toast efímero).
 *   Los pasos del modal requieren acción manual persistente (limpiar caché
 *   Chrome, reiniciar Chrome, volver a la página) → un modal es mejor UX
 *   que un toast de 15s que se cierra solo.
 */

'use client';

import { useState, useEffect } from 'react';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StaleWebApkDialog } from './StaleWebApkDialog';

type InstallAppButtonVariant = 'default' | 'outline' | 'secondary' | 'ghost';
type InstallAppButtonSize = 'default' | 'sm' | 'lg' | 'icon';

export interface InstallAppButtonProps {
  /** Visual variant passed to <Button> — same API as shadcn/ui Button. */
  variant?: InstallAppButtonVariant;
  /** Size passed to <Button> — same API as shadcn/ui Button. */
  size?: InstallAppButtonSize;
  /** Extra className(s) appended to the Button. */
  className?: string;
  /** Label rendered next to the Download icon — default "Instalar App". */
  label?: string;
}

/**
 * Persistent "Instalar App" CTA for the public landing. Renders null
 * pre-mount (anti-P-147), when already installed, or when the browser
 * cannot install a PWA (Firefox/Safari/desktop). Reuses `useInstallPrompt`
 * — no business logic of its own.
 */
export function InstallAppButton({
  variant = 'outline',
  size = 'lg',
  className = '',
  label = 'Instalar App',
}: InstallAppButtonProps) {
  // P-196 (SPEC-130): el hook ahora retorna `webapkStale` — true cuando
  // Chrome reporta WebAPK (getInstalledRelatedApps) PERO beforeinstallprompt
  // no disparó tras 2s (estado stale tras desinstalar).
  const { isInstallable, isInstalled, webapkStale, install } = useInstallPrompt();
  const [mounted, setMounted] = useState(false);
  // P-196 (SPEC-130): estado del modal StaleWebApkDialog — se abre cuando
  // el usuario click en "Instalar App" Y webapkStale=true (en lugar de
  // llamar `install()` que solo mostraría el toast efímero).
  const [showStaleDialog, setShowStaleDialog] = useState(false);

  // Mount gate (P-147 anti-hydration) — do NOT read useInstallPrompt
  // state in SSR. The hook itself has SSR guards (typeof window checks),
  // but the useState initializer `consumeCapturedPrompt()` and the
  // `chromeReportsInstalled` flag can diverge between SSR and client →
  // gate the whole render until `useEffect` confirms we're on the client.
  useEffect(() => {
    setMounted(true);
  }, []);

  // SSR or pre-mount → render nothing (avoids hydration mismatch).
  if (!mounted) return null;

  // Already installed (display-mode standalone or appinstalled fired) →
  // hide the button — there's nothing to install.
  if (isInstalled) return null;

  // Not installable AND Chrome doesn't report a stale WebAPK → the
  // browser can't install a PWA (Firefox/Safari/desktop without PWA
  // support). Hide the button (graceful degradation — no false positive).
  if (!isInstallable) return null;

  // P-196 (SPEC-130): si webapkStale es true, abrimos el modal con los 3
  // pasos accionables en lugar de llamar `install()`. `install()` vería
  // `deferredPrompt=null` (beforeinstallprompt no disparó) y mostraría el
  // toast warning efímero — el modal persiste hasta que el usuario lo
  // cierra, mejor UX para pasos secuenciales manuales.
  const handleClick = () => {
    if (webapkStale) {
      setShowStaleDialog(true);
      return;
    }
    install();
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        onClick={handleClick}
      >
        <Download className="mr-2 size-4" />
        {label}
      </Button>
      <StaleWebApkDialog
        open={showStaleDialog}
        onClose={() => setShowStaleDialog(false)}
      />
    </>
  );
}
